import { economyFetch as fetch } from './economyClient';
/**
 * 質屋マーケットプレイス サーバー連携
 *
 * Edge Function（pawnshop）と連携し、
 * マーケットプレイスの閲覧・購入・買い戻しを行う
 */

// ========================================
// Supabase設定
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 30000;

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.access_token || null;
    }
  } catch (err) {
    console.error('[usePawnShopServer] Failed to get access token:', err);
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

export interface PawnShopItem {
  id: string;
  card_id: string;
  rarity: string;
  quantity: number;
  base_price: number;
  sale_price: number;
  repayment_value: number;
  original_owner_id: string;
  listed_at: string;
  card_name: string | null;
  attribute: string | null;
  card_type: string | null;
  status: 'available' | 'sold' | 'expired' | 'bought_back';
  is_mine: boolean;
}

export interface MarketplaceFilter {
  attribute?: string;
  rarity?: string;
  min_price?: number;
  max_price?: number;
  card_type?: string;
}

export interface MarketplaceResponse {
  success: boolean;
  error?: string;
  items?: PawnShopItem[];
  total?: number;
  has_more?: boolean;
}

export interface InventoryResponse {
  success: boolean;
  error?: string;
  items?: PawnShopItem[];
  total_value?: number;
  available_count?: number;
  sold_count?: number;
}

export interface PurchaseResponse {
  success: boolean;
  error?: string;
  purchased?: {
    card_id: string;
    rarity: string;
    quantity: number;
    price: number;
  };
  was_buyback?: boolean;
  buyback_discount?: number;
  new_gold_balance?: number;
}

// ========================================
// API関数
// ========================================

/**
 * マーケットプレイス一覧を取得
 */
export async function fetchMarketplace(
  filter?: MarketplaceFilter,
  sort?: 'price_asc' | 'price_desc' | 'listed_desc' | 'rarity',
  limit?: number,
  offset?: number
): Promise<MarketplaceResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[fetchMarketplace] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    // クエリパラメータ構築
    const params = new URLSearchParams();
    if (filter?.attribute) params.set('attribute', filter.attribute);
    if (filter?.rarity) params.set('rarity', filter.rarity);
    if (filter?.min_price) params.set('min_price', filter.min_price.toString());
    if (filter?.max_price) params.set('max_price', filter.max_price.toString());
    if (filter?.card_type) params.set('card_type', filter.card_type);
    if (sort) params.set('sort', sort);
    if (limit) params.set('limit', limit.toString());
    if (offset) params.set('offset', offset.toString());

    const queryString = params.toString();
    const url = `${SUPABASE_URL}/functions/v1/pawnshop/marketplace${queryString ? `?${queryString}` : ''}`;

    const response = await withTimeout(
      fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }),
      API_TIMEOUT_MS,
      'fetchMarketplace'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[fetchMarketplace] Marketplace fetched:', { count: data.items?.length });

    return data;
  } catch (err) {
    console.error('[fetchMarketplace] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch marketplace',
    };
  }
}

/**
 * 自分の出品商品一覧を取得
 */
export async function fetchOwnInventory(): Promise<InventoryResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[fetchOwnInventory] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/pawnshop/inventory`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }),
      API_TIMEOUT_MS,
      'fetchOwnInventory'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[fetchOwnInventory] Inventory fetched:', { count: data.items?.length });

    return data;
  } catch (err) {
    console.error('[fetchOwnInventory] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch inventory',
    };
  }
}

/**
 * 商品を購入
 */
export async function purchaseItem(inventoryId: string): Promise<PurchaseResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[purchaseItem] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/pawnshop/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ inventory_id: inventoryId }),
      }),
      API_TIMEOUT_MS,
      'purchaseItem'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[purchaseItem] Purchase completed:', data.purchased);

    return data;
  } catch (err) {
    console.error('[purchaseItem] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to purchase item',
    };
  }
}

/**
 * 商品を買い戻し（元の持ち主が優先価格で購入）
 */
export async function buybackItem(inventoryId: string): Promise<PurchaseResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[buybackItem] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/pawnshop/buyback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ inventory_id: inventoryId }),
      }),
      API_TIMEOUT_MS,
      'buybackItem'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[buybackItem] Buyback completed:', data.purchased);

    return data;
  } catch (err) {
    console.error('[buybackItem] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to buyback item',
    };
  }
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * 認証済みかチェック
 */
export function isPawnShopServerAvailable(): boolean {
  return getAccessToken() !== null;
}

// ========================================
// エクスポート
// ========================================

export default {
  fetchMarketplace,
  fetchOwnInventory,
  purchaseItem,
  buybackItem,
  isPawnShopServerAvailable,
};
